import React, { ReactElement, useEffect, useState } from 'react';
import { Alert, TileRadioAccessible, ReviewAccordionFooter } from '@components';
import { Field, getIn, useFormikContext } from 'formik';
import { tile } from '@interfaces/interfaces';
import { iReviewFormValues } from '@services/ReviewService';

import gridStyles from '@pages/forms/Grid.module.scss';
import InfoIcon from '../../../../../images/info.svg';
import ErrorIcon from '../../../../../images/issue.svg';
import styles from './Step6Grounds.module.scss';

const SPECIAL_CIRCS_FIELD_NAME = 'SelectedGroundInfo4';
const FIELD_NAME = 'selectTheOption';

const SpecialCircumstances = (): ReactElement => {
  const { errors, touched, values }: { errors:any, touched: any, values: iReviewFormValues } = useFormikContext();
  const [exceptionalCicumstancesErrorCount, setExceptionalCicumstancesErrorCount] = useState(0);
  const [errorsBlockingSelection, setErrorsBlockingSelection] = useState(false);
  const formikContext = useFormikContext();

  const TILE_DATA: tile[] = [
    {
      body: 'Yes',
      value: 'true',
    },
    {
      body: 'No',
      value: 'false',
    },
  ];
  useEffect(() => {
    formikContext.setFieldValue('errorOrInfoAppearsInAccordion', exceptionalCicumstancesErrorCount);
    if (values.wereThereSpecialCircumstances === 'false') {
      if (getIn(values, FIELD_NAME).includes(SPECIAL_CIRCS_FIELD_NAME)) formikContext.setFieldValue(FIELD_NAME, []);
      setErrorsBlockingSelection(true);
    }
    if (values.wereThereSpecialCircumstances === 'true') setErrorsBlockingSelection(false);
    setExceptionalCicumstancesErrorCount(exceptionalCicumstancesErrorCount + 1);
  },
  [
    values.wereThereSpecialCircumstances,
    values.specialsCanUploadSupportingDocs,
  ]);

  const wereThereSpecialCircumstancesTitle = (
    <div className={getIn(errors, 'wereThereSpecialCircumstances') && getIn(touched, 'wereThereSpecialCircumstances') && styles.titleError}>
      <p><b>Do one or more of these special circumstances apply to you?</b></p>
      <p className={styles.subtitle}>
        At the time of your offence:
        <ul>
          <li>you had a mental or intellectual disability, disorder, disease or illness</li>
          <li>you had a serious addiction to drugs, alcohol, or a volatile substance</li>
          <li>you were homeless </li>
          <li>
            you were a victim of family violence
            <span className={styles.superscript}>*</span>
          </li>
        </ul>
      </p>
    </div>
  );

  const specialsCanUploadSupportingDocsTitle = (
    <div className={getIn(errors, 'specialsCanUploadSupportingDocs') && getIn(touched, 'specialsCanUploadSupportingDocs') && styles.titleError}>
      <p><b>Can you upload documents to support this? </b></p>
      <p className={styles.subtitle}>
        Some examples of supporting documents are:
        <ul>
          <li>report from a qualified medical practitioner</li>
          <li>report from a social worker or support agency</li>
          <li>a police report</li>
        </ul>
      </p>
    </div>
  );

  const wereThereSpecialCircumstancesMessage = (
    <p>Based on the information provided, you are not eligible to request a review under this ground.</p>
  );
  const specialsCanUploadSupportingDocsMessage = (
    <p>Your application may not be successful unless you provide evidence.</p>
  );

  return (
    <div>
      <div className={gridStyles.form}>
        {wereThereSpecialCircumstancesTitle}
        <Field
          name="wereThereSpecialCircumstances"
          title=""
          component={TileRadioAccessible}
          tileData={TILE_DATA}
          orientationRow
        />
        <div className={styles.alertMessageBox}>
          {values.wereThereSpecialCircumstances === 'false' && (
            <Alert
              message={wereThereSpecialCircumstancesMessage}
              theme="error"
              title=""
              icon={ErrorIcon}
              classAlt={styles.boxheight}
              // color="black"
            />
          )}
        </div>
        {specialsCanUploadSupportingDocsTitle}
        <Field
          name="specialsCanUploadSupportingDocs"
          title=""
          component={TileRadioAccessible}
          tileData={TILE_DATA}
          orientationRow
        />
        <div className={styles.alertMessageBox}>
          {values.specialsCanUploadSupportingDocs === 'false' && (
            <Alert
              message={specialsCanUploadSupportingDocsMessage}
              theme="info"
              title=""
              icon={InfoIcon}
              classAlt={styles.boxheight}
              // color="black"
            />
          )}
        </div>
        <p>
          For more information on what evidence is required, the application process and what the legislation says, see
          {' '}
          <a href="/Support/Special-circumstances" target="_blank" rel="noopener noreferrer">
            Special Circumstances
          </a>
          .
        </p>

        <br />
        <span><b>Things to note:</b></span>
        <ul>
          <li>
            If your application for enforcement review on the ground of special circumstances is successful,
            {' '}
            <b>any demerit points will remain on your licence.</b>
          </li>
          <br />
          <li>
            If your fines resulted from family violence, you may be able to apply under the
            {' '}
            <a href="/Support/Family-Violence-Scheme" target="_blank" rel="noopener noreferrer">
              Family Violence Scheme
            </a>
            .
          </li>
          <br />
          <li>
            If you have other reasons for requesting a review, you may be able to apply under
            {' '}
            <a href="/Your-options/Request-a-review" target="_blank" rel="noopener noreferrer">
              Exceptional Circumstances
            </a>
            .
          </li>
        </ul>

        <ReviewAccordionFooter
          selectOptionName={FIELD_NAME}
          closeOptionName="closeTheOption"
          itemOption={SPECIAL_CIRCS_FIELD_NAME}
          isSelectButtonDisabled={errorsBlockingSelection}
          validateFieldsNames={['wereThereSpecialCircumstances', 'specialsCanUploadSupportingDocs']}
          isMultiSelect={false}
        />
      </div>
    </div>
  );
};

export default SpecialCircumstances;
